import { motion } from "framer-motion";
import { Trophy, Timer, Sparkles, RotateCcw, Home, Share2, Users, Medal } from "lucide-react";
import { toast } from "sonner";
import { formatTime, DIFFICULTY_CONFIG } from "@/lib/scoring";

export default function GameOverDialog({
  result,
  phrase,
  seconds,
  difficulty,
  isDaily = false,
  dailyMeta = null,
  onPlayAgain,
  onHome,
}) {
  const cfg = DIFFICULTY_CONFIG[difficulty] || DIFFICULTY_CONFIG.easy;
  const daily = result.daily; // {date, plays_today, your_rank, percentile, best_score_today, your_best_today}

  const handleShare = async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const challengeUrl = origin
      ? isDaily
        ? `${origin}/play/daily`
        : `${origin}/play/${difficulty}`
      : "";
    // Don't leak the phrase — just tease the shape of it.
    const wordCount = phrase.trim().split(/\s+/).length;
    const letterCount = phrase.replace(/\s+/g, "").length;
    const teaser = `${wordCount} word${wordCount === 1 ? "" : "s"} · ${letterCount} letters`;
    const dailyLine =
      isDaily && daily
        ? `\nRank #${daily.your_rank}/${daily.plays_today} — ${daily.percentile}th percentile today`
        : "";
    const shareText =
      (isDaily ? `Fraseology · Daily ${daily?.date || ""}` : `Fraseology — ${cfg.label}`) +
      `\n${teaser}` +
      `\nSolved in ${formatTime(seconds)} for ${result.total} pts` +
      (result.underMinute ? " (under-minute bonus!)" : "") +
      dailyLine +
      (challengeUrl ? `\nCan you beat me? → ${challengeUrl}` : "");
    const shareData = {
      title: "Fraseology",
      text: shareText,
      url: challengeUrl || undefined,
    };
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        // eslint-disable-next-line no-console
        console.warn("[share] native share failed, falling back to clipboard:", err);
      } else {
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Result copied to clipboard", {
        description: "Paste it anywhere to share.",
      });
    } catch {
      toast.error("Couldn't copy. Long-press to share manually.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A1A1A]/70 backdrop-blur-sm"
      data-testid="game-over-dialog"
    >
      <motion.div
        initial={{ scale: 0.9, y: 14, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 240, damping: 22 }}
        className="relative w-full max-w-lg bg-[#F4F1EA] border-2 border-[#1A1A1A] shadow-[10px_10px_0px_#EB5E28]"
      >
        <div className="border-b-2 border-[#1A1A1A] px-6 py-3 flex items-center justify-between bg-[#1A1A1A] text-[#F4F1EA]">
          <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] font-bold text-[#EB5E28]">
            {isDaily ? "Daily edition" : "Final Edition"}
          </span>
          <span className="font-serif-display italic text-sm">
            {isDaily ? (daily?.date || "Today") : `${cfg.label} · Volume`}
          </span>
        </div>

        <div className="px-6 md:px-8 py-7 md:py-9">
          <p className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-[#6B6A68] font-semibold">
            Phrase solved
          </p>
          <h2
            className="font-serif-display text-3xl md:text-4xl font-semibold mt-1 leading-tight"
            data-testid="game-over-phrase"
          >
            {phrase}
          </h2>

          <div className="mt-7 grid grid-cols-2 gap-3">
            <div className="border-2 border-[#1A1A1A] bg-white px-4 py-3 shadow-[3px_3px_0px_#1A1A1A]">
              <div className="flex items-center gap-1.5 mb-1 text-[#6B6A68]">
                <Timer className="w-3.5 h-3.5" />
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.2em] font-semibold">
                  Time
                </span>
              </div>
              <div className="font-serif-display text-3xl font-semibold tabular-nums" data-testid="final-time">
                {formatTime(seconds)}
              </div>
            </div>
            <div className="border-2 border-[#1A1A1A] bg-[#1A1A1A] text-[#F4F1EA] px-4 py-3 shadow-[3px_3px_0px_#EB5E28]">
              <div className="flex items-center gap-1.5 mb-1 text-[#EB5E28]">
                <Trophy className="w-3.5 h-3.5" />
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.2em] font-semibold">
                  Score
                </span>
              </div>
              <div className="font-serif-display text-3xl font-semibold tabular-nums" data-testid="final-score">
                {result.total}
              </div>
            </div>
          </div>

          {/* Breakdown */}
          <div className="mt-6 border-t-2 border-dashed border-[#1A1A1A]/30 pt-4 space-y-1.5 font-sans-ui text-sm">
            <Row label="Base" value={`+${result.base}`} />
            <Row label="Letters" value={`+${result.letters}`} />
            <Row label="Time penalty" value={`-${result.timePenalty}`} />
            {result.underMinute && (
              <Row
                label={
                  <span className="flex items-center gap-1.5 text-[#EB5E28] font-semibold">
                    <Sparkles className="w-3.5 h-3.5" />
                    Under-minute bonus
                  </span>
                }
                value={`+${result.bonus}`}
                accent
              />
            )}
            <div className="h-px bg-[#1A1A1A]/30 my-2" />
            <Row label={<span className="font-bold">Total</span>} value={result.total} big />
          </div>

          {isDaily && daily && (
            <div
              className="mt-6 border-2 border-[#1A1A1A] bg-[#F4F1EA] p-4"
              data-testid="daily-rank-card"
            >
              <div className="flex items-center gap-2 mb-2">
                <Medal className="w-4 h-4 text-[#EB5E28]" />
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.24em] font-bold text-[#EB5E28]">
                  Today&apos;s leaderboard
                </span>
              </div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span
                  className="font-serif-display text-4xl font-bold tabular-nums"
                  data-testid="daily-rank"
                >
                  #{daily.your_rank}
                </span>
                <span className="font-serif-display italic text-lg text-[#6B6A68]">
                  of {daily.plays_today}
                </span>
                <span
                  className="ml-auto px-2 py-1 bg-[#0F5132] text-[#F4F1EA] font-sans-ui text-xs uppercase tracking-[0.2em] font-bold"
                  data-testid="daily-percentile"
                >
                  {daily.percentile}th percentile
                </span>
              </div>
              <div className="mt-2 flex items-center gap-1.5 font-sans-ui text-xs text-[#6B6A68]">
                <Users className="w-3 h-3" />
                {daily.plays_today === 1
                  ? "You're the first today — come back tomorrow for another."
                  : daily.your_rank === 1
                  ? "You're in the lead today."
                  : `${daily.plays_today - daily.your_rank} player${daily.plays_today - daily.your_rank === 1 ? "" : "s"} behind you.`}
              </div>
            </div>
          )}

          <div className="mt-7 flex items-center gap-3 flex-wrap">
            <button
              onClick={onHome}
              data-testid="home-btn-end"
              className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 px-4 py-3 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] font-sans-ui font-semibold uppercase tracking-[0.15em] text-xs shadow-[3px_3px_0px_#1A1A1A] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#1A1A1A] active:translate-y-0 active:shadow-[0px_0px_0px_#1A1A1A] transition-all"
            >
              <Home className="w-4 h-4" />
              Home
            </button>
            <button
              onClick={handleShare}
              data-testid="share-result-btn"
              className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#1A1A1A] text-[#F4F1EA] border-2 border-[#1A1A1A] font-sans-ui font-semibold uppercase tracking-[0.15em] text-xs shadow-[3px_3px_0px_#EB5E28] hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#EB5E28] active:translate-y-0 active:shadow-[0px_0px_0px_#EB5E28] transition-all"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
            <button
              onClick={onPlayAgain}
              data-testid="play-again-btn"
              className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#EB5E28] text-[#F4F1EA] border-2 border-[#1A1A1A] font-sans-ui font-bold uppercase tracking-[0.15em] text-xs shadow-[4px_4px_0px_#1A1A1A] hover:-translate-y-0.5 hover:shadow-[6px_6px_0px_#1A1A1A] active:translate-y-0 active:shadow-[0px_0px_0px_#1A1A1A] transition-all"
            >
              <RotateCcw className="w-4 h-4" />
              Play again
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Row({ label, value, accent, big }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`${big ? "text-base" : "text-sm"} text-[#1A1A1A]`}>{label}</span>
      <span
        className={`tabular-nums ${big ? "font-serif-display text-2xl font-bold" : accent ? "font-semibold text-[#EB5E28]" : "text-[#1A1A1A]"}`}
      >
        {value}
      </span>
    </div>
  );
}
